import { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Popconfirm,
  Row,
  Col,
  Typography,
  Empty,
  Image,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import usePreschoolGrading from "../../../hooks/academics/usePreschoolGrading";
import domainSettings from "../../../dependencies/custom/settings";

const { Title, Text } = Typography;
const { TextArea } = Input;

const PreschoolGradingManagement = () => {
  const {
    loading,
    learningAreas,
    preschoolGrades,
    fetchLearningAreas,
    saveLearningArea,
    deleteLearningArea,
    fetchPreschoolGrades,
    savePreschoolGrade,
    deletePreschoolGrade,
  } = usePreschoolGrading();

  const [gradeModalVisible, setGradeModalVisible] = useState(false);
  const [areaModalVisible, setAreaModalVisible] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  const [gradeForm] = Form.useForm();
  const [areaForm] = Form.useForm();
  const [iconPreviewVisible, setIconPreviewVisible] = useState(false);
  const [previewArea, setPreviewArea] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconPreviewUrl, setIconPreviewUrl] = useState(null);
  const [existingIconUrl, setExistingIconUrl] = useState(null);

  const getIconUrl = (url) => {
    if (!url || typeof url !== "string") return null;
    if (
      url.startsWith("http://") ||
      url.startsWith("https://") ||
      url.startsWith("//") ||
      url.startsWith("data:") ||
      url.startsWith("blob:")
    ) {
      return url;
    }
    const backend = String(domainSettings?.backend || "").replace(/\/+$/, "");
    if (!backend) return url;
    return `${backend}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const clearIconSelection = () => {
    if (iconPreviewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(iconPreviewUrl);
    }
    setIconFile(null);
    setIconPreviewUrl(null);
    setExistingIconUrl(null);
  };

  useEffect(() => {
    fetchLearningAreas();
    fetchPreschoolGrades();
  }, []);

  // Preschool Grade handlers
  const handleAddGrade = () => {
    setEditingGrade(null);
    gradeForm.resetFields();
    setGradeModalVisible(true);
  };

  const handleEditGrade = (record) => {
    setEditingGrade(record);
    gradeForm.setFieldsValue({
      letterGrade: record.letterGrade,
      remark: record.remark,
    });
    setGradeModalVisible(true);
  };

  const handleGradeSubmit = async () => {
    try {
      const values = await gradeForm.validateFields();
      const data = editingGrade ? { id: editingGrade.id, ...values } : values;
      const result = await savePreschoolGrade(data);
      if (result) {
        setGradeModalVisible(false);
        gradeForm.resetFields();
        setEditingGrade(null);
        fetchPreschoolGrades();
      }
    } catch (error) {
      // validation error
    }
  };

  const handleDeleteGrade = async (id) => {
    const success = await deletePreschoolGrade(id);
    if (success) {
      fetchPreschoolGrades();
    }
  };

  // Learning Area handlers
  const handleAddArea = () => {
    setEditingArea(null);
    areaForm.resetFields();
    clearIconSelection();
    setAreaModalVisible(true);
  };

  const handleEditArea = (record) => {
    setEditingArea(record);
    areaForm.setFieldsValue({
      columnName: record.columnName,
    });
    clearIconSelection();
    setExistingIconUrl(getIconUrl(record.icon));
    setAreaModalVisible(true);
  };

  const handleViewIcon = (record) => {
    setPreviewArea(record);
    setIconPreviewVisible(true);
  };

  const handleAreaSubmit = async () => {
    try {
      const values = await areaForm.validateFields();
      const data = editingArea ? { id: editingArea.id, ...values } : values;
      if (iconFile) {
        data.icon = iconFile;
      }
      const result = await saveLearningArea(data);
      if (result) {
        setAreaModalVisible(false);
        areaForm.resetFields();
        setEditingArea(null);
        clearIconSelection();
        fetchLearningAreas();
      }
    } catch (error) {
      // validation error
    }
  };

  const handleDeleteArea = async (id) => {
    const success = await deleteLearningArea(id);
    if (success) {
      fetchLearningAreas();
    }
  };

  const getGradeColor = (letter) => {
    const colors = {
      A: "green",
      B: "blue",
      C: "gold",
      D: "orange",
      E: "red",
      F: "red",
    };
    return colors[letter?.toUpperCase()] || "default";
  };

  const gradeColumns = [
    {
      title: "Grade",
      dataIndex: "letterGrade",
      key: "letterGrade",
      width: 80,
      render: (text) => (
        <Tag
          color={getGradeColor(text)}
          style={{ fontWeight: 700, fontSize: 14 }}
        >
          {text}
        </Tag>
      ),
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditGrade(record)}
          />
          <Popconfirm
            title="Delete this grade?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteGrade(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const areaColumns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_, __, index) => (
        <Text type="secondary" style={{ fontFamily: "monospace" }}>
          {String(index + 1).padStart(2, "0")}
        </Text>
      ),
    },
    {
      title: "Learning Area",
      dataIndex: "columnName",
      key: "columnName",
    },
    {
      title: "Icon",
      dataIndex: "icon",
      key: "icon",
      width: 70,
      render: (icon, record) => {
        const url = getIconUrl(icon);
        return url ? (
          <Image
            src={url}
            alt={record.columnName || "Learning area icon"}
            width={40}
            height={40}
            style={{ objectFit: "contain", borderRadius: 4 }}
            preview={false}
            placeholder={<Text type="secondary">Loading…</Text>}
            onClick={() => handleViewIcon(record)}
          />
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 130,
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            disabled={!record.icon}
            title={record.icon ? "View icon" : "No icon"}
            onClick={() => handleViewIcon(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditArea(record)}
          />
          <Popconfirm
            title="Delete this learning area?"
            description="This action cannot be undone."
            onConfirm={() => handleDeleteArea(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={4} style={{ marginBottom: 4 }}>
          Nursery Grading
        </Title>
        <Text type="secondary">
          Manage grade remarks and learning area columns for nursery
          assessment
        </Text>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>Nursery Grades</span>
                <Tag color="green">{preschoolGrades.length}</Tag>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={handleAddGrade}
              >
                Add Grade
              </Button>
            }
          >
            <Table
              dataSource={preschoolGrades}
              columns={gradeColumns}
              rowKey="id"
              loading={loading}
              pagination={false}
              locale={{
                emptyText: <Empty description="No grades defined yet" />,
              }}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <span>Learning Areas</span>
                <Tag color="green">{learningAreas.length}</Tag>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={handleAddArea}
              >
                Add Area
              </Button>
            }
          >
            <Table
              dataSource={learningAreas}
              columns={areaColumns}
              rowKey="id"
              loading={loading}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty description="No learning areas defined yet" />
                ),
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Preschool Grade Modal */}
      <Modal
        title={editingGrade ? "Edit Nursery Grade" : "Add Nursery Grade"}
        open={gradeModalVisible}
        onCancel={() => {
          setGradeModalVisible(false);
          gradeForm.resetFields();
          setEditingGrade(null);
        }}
        onOk={handleGradeSubmit}
        okText={editingGrade ? "Update" : "Add"}
        confirmLoading={loading}
      >
        <Form form={gradeForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="letterGrade"
                label="Grade Letter"
                rules={[{ required: true, message: "Enter grade letter" }]}
              >
                <Input placeholder="e.g. A" maxLength={2} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="remark"
                label="Remark"
                rules={[{ required: true, message: "Enter remark" }]}
              >
                <Input placeholder="e.g. Excellent" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Learning Area Modal */}
      <Modal
        title={editingArea ? "Edit Learning Area" : "Add Learning Area"}
        open={areaModalVisible}
        onCancel={() => {
          setAreaModalVisible(false);
          areaForm.resetFields();
          setEditingArea(null);
          clearIconSelection();
        }}
        onOk={handleAreaSubmit}
        okText={editingArea ? "Update" : "Add"}
        confirmLoading={loading}
      >
        <Form form={areaForm} layout="vertical">
          <Form.Item
            name="columnName"
            label="Area Description"
            rules={[
              { required: true, message: "Enter learning area description" },
            ]}
          >
            <TextArea
              rows={3}
              placeholder="e.g. Developing and using my language appropriately."
            />
          </Form.Item>
          <Form.Item
            label={
              <span>
                Icon{" "}
                <Text
                  type="secondary"
                  style={{ fontSize: 14, fontWeight: 400 }}
                >
                  (Leave empty to keep the current icon)
                </Text>
              </span>
            }
          >
            <Upload
              accept="image/*"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                if (iconPreviewUrl?.startsWith("blob:")) {
                  URL.revokeObjectURL(iconPreviewUrl);
                }
                setIconFile(file);
                setIconPreviewUrl(URL.createObjectURL(file));
                return false;
              }}
              onRemove={() => {
                if (iconPreviewUrl?.startsWith("blob:")) {
                  URL.revokeObjectURL(iconPreviewUrl);
                }
                setIconFile(null);
                setIconPreviewUrl(null);
              }}
            >
              <Button icon={<UploadOutlined />}>
                {iconFile || existingIconUrl ? "Change icon" : "Upload icon"}
              </Button>
            </Upload>
            {(iconPreviewUrl || (!iconFile && existingIconUrl)) && (
              <div style={{ marginTop: 8 }}>
                <Image
                  src={iconPreviewUrl || existingIconUrl}
                  alt="icon preview"
                  width={80}
                  style={{ objectFit: "contain" }}
                  preview={false}
                />
                {iconFile && (
                  <div>
                    <Button
                      type="link"
                      size="small"
                      danger
                      onClick={() => {
                        if (iconPreviewUrl?.startsWith("blob:")) {
                          URL.revokeObjectURL(iconPreviewUrl);
                        }
                        setIconFile(null);
                        setIconPreviewUrl(null);
                      }}
                    >
                      Remove selected
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Icon Preview Modal */}
      <Modal
        title={previewArea?.columnName || "Icon preview"}
        open={iconPreviewVisible}
        footer={[
          <Button key="close" onClick={() => setIconPreviewVisible(false)}>
            Close
          </Button>,
        ]}
        onCancel={() => setIconPreviewVisible(false)}
      >
        {getIconUrl(previewArea?.icon) ? (
          <div style={{ textAlign: "center" }}>
            <Image
              src={getIconUrl(previewArea?.icon)}
              alt={previewArea?.columnName || "learning area icon"}
              style={{ maxWidth: "100%", objectFit: "contain" }}
            />
          </div>
        ) : (
          <Empty description="No icon for this learning area" />
        )}
      </Modal>
    </div>
  );
};

export default PreschoolGradingManagement;
